export interface SystemKeyOption {
  label: string
  value: number | string
  codeKey?: string
  id?: number
}

export const getListComboByKey = (key: string | string[], listSystemKey: any[] = []) => {
  if (!listSystemKey || !Array.isArray(listSystemKey) || listSystemKey.length === 0) return []
  const keys = Array.isArray(key) ? key : [key]

  for (const k of keys) {
    const parent = listSystemKey.find(
      (x: any) => String(x.codeKey).trim().toUpperCase() === String(k).trim().toUpperCase()
    )
    if (parent) {
      const children = listSystemKey
        .filter((x: any) => (x.parentId ?? x.parentID) === parent.id)
        .sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      if (children.length > 0) return children
    }
  }
  return []
}

export const getSystemKeyOptions = (
  key: string | string[],
  listSystemKey: any[] = [],
  fallbackOptions: { label: string; value: any }[] = []
): SystemKeyOption[] => {
  const list = getListComboByKey(key, listSystemKey)
  if (list && list.length > 0) {
    return list.map((item: any) => ({
      label: item.description || item.codeKey,
      value: item.codeValue,
      codeKey: item.codeKey,
      id: item.id,
    }))
  }
  return fallbackOptions
}

export const getSystemKeyDescription = (
  key: string | string[],
  codeValue: any,
  listSystemKey: any[] = [],
  fallback?: string
): string => {
  if (codeValue === undefined || codeValue === null || codeValue === '') {
    return fallback ?? ''
  }
  const list = getListComboByKey(key, listSystemKey)
  if (list && list.length > 0) {
    const found = list.find(
      (item: any) =>
        Number(item.codeValue) === Number(codeValue) ||
        String(item.codeKey).toUpperCase() === String(codeValue).toUpperCase()
    )
    if (found?.description) return found.description
  }
  return fallback ?? String(codeValue)
}

export const getSystemKeyItemByCodeKey = (codeKey: string, listSystemKey: any[] = []) => {
  if (!codeKey || !Array.isArray(listSystemKey)) return undefined
  return listSystemKey.find(
    (x: any) => String(x.codeKey).trim().toUpperCase() === String(codeKey).trim().toUpperCase()
  )
}

/**
 * Lấy toàn bộ item SystemKey con theo key cha và codeValue
 */
export const getSystemKeyItem = (
  key: string | string[],
  codeValue: any,
  listSystemKey: any[] = []
) => {
  if (codeValue === undefined || codeValue === null || codeValue === '') {
    return undefined
  }
  const list = getListComboByKey(key, listSystemKey)
  if (list && list.length > 0) {
    return list.find(
      (item: any) =>
        Number(item.codeValue) === Number(codeValue) ||
        String(item.codeKey).toUpperCase() === String(codeValue).toUpperCase()
    )
  }
  return undefined
}

import { normalizeColorToSemantic } from '@/shared/config'

/**
 * Lấy mã màu semantic (color) được định cấu hình trong SystemKey.
 * Tự động quy đổi các màu vật lý cũ (green, red, gold, blue...) sang Semantic Token (success, error, warning, processing...).
 */
export const getSystemKeyColor = (
  key: string | string[],
  codeValue: any,
  listSystemKey: any[] = [],
  fallbackColor = 'default'
): string => {
  const item = getSystemKeyItem(key, codeValue, listSystemKey)
  const color = item?.color ?? item?.Color
  if (color && typeof color === 'string' && color.trim() !== '') {
    return normalizeColorToSemantic(color)
  }
  return normalizeColorToSemantic(fallbackColor)
}

/**
 * Lấy mã biểu tượng (icon) được định cấu hình trong SystemKey.
 * Trả về tên icon string nếu có (vd: 'LockOutlined', 'EditOutlined'), hoặc fallbackIcon nếu không có.
 */
export const getSystemKeyIcon = (
  key: string | string[],
  codeValue: any,
  listSystemKey: any[] = [],
  fallbackIcon: string | null = null
): string | null => {
  const item = getSystemKeyItem(key, codeValue, listSystemKey)
  const icon = item?.icon ?? item?.Icon
  if (icon && typeof icon === 'string' && icon.trim() !== '') {
    return icon.trim()
  }
  return fallbackIcon
}
